<template>
    <div v-if="isLoaded" class="reportConfigurationContent content-wrapper">
        <div class="common-form-wrapper no-print">
            <div class="wrapper">
                <Multiselect
                    v-model="dataOfReport.aAreas"
                    :clear-on-select="true"
                    :close-on-select="false"
                    label="name"
                    :multiple="true"
                    :options="aAreas"
                    :placeholder="$t('reporting.commonLabels.allAA')"
                    :searchable="false"
                    :taggable="true"
                    track-by="id"
                />
            </div>
            <div class="wrapper">
                <span v-if="$v.dataOfReport.emails.$invalid" class="tooltiptext">
                    {{ errorDisplay($v.dataOfReport.emails) }}
                </span>
                <Multiselect
                    :class="[{ tooltiperror: $v.dataOfReport.emails.$invalid }]"
                    :clear-on-select="true"
                    :close-on-select="false"
                    :max="5"
                    :multiple="true"
                    :options="dataOfReport.emails"
                    :placeholder="$t('notCheckIn.reportConfigurationTab.sendEmailTo')"
                    :searchable="true"
                    :tag-placeholder="$t('reporting.commonLabels.pressEnterToAddEmail')"
                    :taggable="true"
                    :value="dataOfReport.emails"
                    @input="updateEmails($event)"
                    @tag="addEmail"
                />
            </div>
        </div>
        <div class="print-only">
            <h2>{{ $t('notCheckIn.reportConfigurationTab.title') }}</h2>
            <table class="report-configuration-print">
                <tbody>
                    <tr>
                        <td>{{ $t('reporting.commonLabels.aAreas') }}:</td>
                        <td>{{ dataOfReportPrinted.aAreas }}</td>
                    </tr>
                    <tr>
                        <td>{{ $t('reporting.commonLabels.emails') }}:</td>
                        <td>{{ dataOfReportPrinted.emails }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script type="text/babel">
import Multiselect from 'moxa-vue-multiselect';
import mappingState from '../common/mappingState';
import { errorDisplay, email } from '../../validators';
import { getCancelToken } from '../../api';
import { mapState } from 'vuex';
import { resourceNotCheckInConfiguration } from '../../api/resources';
import { SUCCESS, ERROR } from '../../constants';
import { ifIsNotCanceled } from '../common/helpers';

export default {
    components: { Multiselect },

    mixins: [mappingState],

    data() {
        return {
            cancelToken: getCancelToken(),
            dataOfReport: {
                // current data (to be saved on server)
                aAreas: [],
                emails: [],
            },
            dataOfReportPrinted: {
                // data (version of the data that kept on server)
                aAreas: '',
                emails: '',
            },
            emailsInvalid: false,
            isLoaded: false,
        };
    },

    validations() {
        return {
            dataOfReport: {
                emails: {
                    email,
                },
            },
        };
    },

    computed: {
        ...mapState({
            aAreas: 'assignmentArea',
            isLoading: 'isLoading',
            unsavedChanges: 'unsavedChanges',
        }),
    },

    watch: {
        dataOfReport: {
            handler() {
                this.setUnsavedChanges(true);
            },
            deep: true,
        },
    },

    created() {
        this.setSaveButton();
        this.loadData();
    },

    beforeDestroy() {
        this.cancelToken.cancel();
    },

    methods: {
        errorDisplay,

        /**
         * Adds trimmed email to emails array for configuration
         * @param email
         */
        addEmail(email) {
            if (email) {
                this.dataOfReport.emails.push(email.trim());
            }
        },

        /**
         * Updates emails array for configuration (called on email remove)
         * @param emails
         */
        updateEmails(emails) {
            this.dataOfReport.emails = emails;
        },

        /**
         * Configures Save button
         */
        setSaveButton() {
            this.configButton([
                {
                    buttonName: 'saveButton',
                    action: () => {
                        this.saveReport();
                    },
                    disabled: () => !this.unsavedChanges || this.$v.dataOfReport.emails.$invalid,
                },
            ]);
        },

        /**
         * Clear page and show Forbidden
         */
        handleResponseForbidden() {
            // Clear page
            this.isLoaded = false;

            // Forget unsaved changes
            this.$nextTick(() => {
                this.setUnsavedChanges(false);
            });

            this.popup({
                type: ERROR,
                header: this.$t('common.error'),
                text: this.$t('error.common.forbidden'),
            });
        },

        /**
         * Load report configuration data from server
         */
        loadData() {
            this.showLoading(true);

            return resourceNotCheckInConfiguration({ cancelToken: this.cancelToken.token })
                .then(({ data }) => {
                    this.dataOfReport.aAreas = this.aAreas.filter(area =>
                        data.assignment_areas.includes(area.id)
                    );
                    this.dataOfReport.emails = data.emails;
                    this.dataOfReportPrinted = this.getDataOfReportPrinted(this.dataOfReport);

                    this.showLoading(false);
                    this.$nextTick(() => {
                        this.setUnsavedChanges(false);
                    });
                    this.isLoaded = true;
                })
                .catch(
                    ifIsNotCanceled({
                        callback: response => {
                            if (response.status === 403) {
                                return this.handleResponseForbidden();
                            }
                            this.isLoaded = true;
                        },
                    })
                );
        },

        /**
         * Saves report configuration on server
         */
        saveReport() {
            this.showLoading(true);

            let allAAreasAreSelected = this.dataOfReport.aAreas.length === this.aAreas.length;

            let data = {
                aAreasIds: allAAreasAreSelected ? [] : this.dataOfReport.aAreas.map(area => area.id),
                emails: this.dataOfReport.emails,
            };

            return resourceNotCheckInConfiguration({
                data,
                cancelToken: this.cancelToken.token,
                method: 'post',
            })
                .then(() => {
                    if (allAAreasAreSelected) {
                        this.dataOfReport.aAreas = [];
                    }
                    this.dataOfReportPrinted = this.getDataOfReportPrinted(this.dataOfReport);

                    this.showLoading(false);
                    this.$nextTick(() => {
                        this.setUnsavedChanges(false);
                    });

                    this.popup({
                        type: SUCCESS,
                        header: this.$t('common.success'),
                        text: this.$t('common.saved'),
                    });
                })
                .catch(
                    ifIsNotCanceled({
                        callback: response => {
                            if (response.status === 403) {
                                return this.handleResponseForbidden();
                            }
                            this.isLoaded = true;
                        },
                    })
                );
        },

        /**
         * Gets printed version of report configuration data
         * @param data
         *
         */
        getDataOfReportPrinted(data) {
            return {
                aAreas:
                    data.aAreas.map(area => area.name).join(', ') || this.$t('reporting.commonLabels.allAA'),
                emails: data.emails.join(', '),
            };
        },
    },
};
</script>
